import { BellRing, Clock, Monitor, Smartphone } from "lucide-react";

function Badge({ icon: Icon, on, label, testid }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${on ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`} data-testid={testid}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

const steps = (m) => { const d = m.devices || {}; return [!!d.phone?.installed, !!d.pc?.installed, !!(d.phone?.alerts || d.pc?.alerts)]; };

export function ago(iso) {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 120) return "active just now";
  if (s < 3600) return `active ${Math.round(s / 60)} min ago`;
  if (s < 86400) return `active ${Math.round(s / 3600)} hr ago`;
  const d = Math.round(s / 86400);
  return d < 30 ? `active ${d} day${d === 1 ? "" : "s"} ago` : `active ${new Date(iso).toLocaleDateString()}`;
}

// "Team fully set up" bar: phone app + PC app + alerts on for every member.
export function TeamReady({ members }) {
  if (!members?.length) return null;
  const done = members.reduce((a, m) => a + steps(m).filter(Boolean).length, 0);
  const pct = Math.round((done / (members.length * 3)) * 100);
  const full = members.filter((m) => steps(m).every(Boolean)).length;
  return (
    <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="team-ready">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">Team set up</p>
        <p className="font-mono text-xs font-bold text-slate-700" data-testid="team-ready-pct">{pct}%</p>
      </div>
      <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full rounded-full transition-[width] duration-700 ${pct === 100 ? "bg-emerald-500" : "bg-[#0053DA]"}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-slate-500" data-testid="team-ready-detail">
        {full} of {members.length} fully set up (phone app, PC app and alerts on){pct === 100 ? " — everyone's ready!" : ""}
      </p>
    </div>
  );
}

// Team page: has this member installed SnapList on a phone / PC, and are alerts on?
export default function DeviceStatus({ m }) {
  const d = m.devices || {};
  const alerts = !!(d.phone?.alerts || d.pc?.alerts);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`team-member-devices-${m.id}`}>
      <Badge icon={Smartphone} on={!!d.phone?.installed} label={d.phone?.installed ? "Phone app" : "No phone app"} testid={`team-member-phone-${m.id}`} />
      <Badge icon={Monitor} on={!!d.pc?.installed} label={d.pc?.installed ? "PC app" : "No PC app"} testid={`team-member-pc-${m.id}`} />
      <Badge icon={BellRing} on={alerts} label={alerts ? "Alerts on" : "Alerts off"} testid={`team-member-alerts-${m.id}`} />
      <span className="inline-flex items-center gap-1 px-1 text-[10px] text-slate-500" data-testid={`team-member-active-${m.id}`}>
        <Clock className="h-3 w-3" /> {ago(m.last_active) || "hasn't opened SnapList yet"}
      </span>
    </div>
  );
}
